import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Cake, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isStandalone } from "@/components/OnboardingDialog";
import { isPushSupported, isPushEnabled, enablePush } from "@/lib/push";
import { useTheme } from "@/contexts/ThemeContext";

const SNOOZE_KEY = "sd_notif_nudge_until";
const SNOOZE_DAYS = 7;

export function NotificationNudge() {
  const { resolved } = useTheme();
  const dark = resolved !== "light";
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Only nudge once the app is on the Home Screen (standalone), where notifications
    // actually work reliably — and only if we haven't asked yet (permission "default").
    if (!isStandalone() || !isPushSupported()) return;
    if (Notification.permission !== "default") return;
    if (Date.now() < Number(localStorage.getItem(SNOOZE_KEY) || 0)) return;
    isPushEnabled().then((on) => { if (!on) setVisible(true); });
  }, []);

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
    setVisible(false);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const res = await enablePush();
      if (res.ok) {
        toast.success("Notifications are on — you'll get your reminders here.");
        setVisible(false);
      } else if (res.reason === "denied") {
        toast.error("Notifications blocked. Enable them in your device settings, then try Settings → Notifications.");
        snooze();
      } else {
        toast.error("Push isn't configured on the server right now.");
        snooze();
      }
    } catch {
      toast.error("Couldn't turn on notifications. Try again from Settings → Notifications.");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-4 shadow-lg ${dark ? "border-[#E0575B]/50 bg-[#9B2226]/20 ring-1 ring-[#E0575B]/20" : "border-[#9B2226]/40 bg-[#9B2226]/10"}`}
      data-testid="notif-nudge"
    >
      <button
        onClick={snooze}
        aria-label="Dismiss"
        data-testid="notif-nudge-dismiss"
        className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-500/10 hover:text-zinc-300"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9B2226]/20 text-[#E0575B]">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className={`font-display text-sm font-800 tracking-tight ${dark ? "text-zinc-50" : "text-zinc-900"}`}>
            Turn on notifications
          </p>
          <p className={`mt-0.5 text-xs ${dark ? "text-zinc-200" : "text-zinc-600"}`}>
            You're all set up on your Home Screen. Switch on alerts so your reminders actually reach you.
          </p>
          <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${dark ? "text-zinc-300" : "text-zinc-500"}`}>
            <span className="flex items-center gap-1"><Cake className="h-3 w-3 text-amber-400" /> Celebrations</span>
            <span className="flex items-center gap-1"><ClipboardCheck className="h-3 w-3 text-[#E0575B]" /> Post-tour reminders</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={turnOn}
              disabled={busy}
              data-testid="notif-nudge-enable"
              className="h-8 gap-1.5 rounded-lg bg-[#9B2226] px-3 text-xs font-700 text-white hover:bg-[#7A1A1E]"
            >
              <Bell className="h-3.5 w-3.5" />
              {busy ? "Turning on…" : "Turn on"}
            </Button>
            <button
              onClick={snooze}
              data-testid="notif-nudge-later"
              className="text-xs font-600 text-zinc-500 transition-colors hover:text-zinc-400"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
